import { pick, get } from 'lodash';
import { objectToQueryParams } from 'utils/urlutils';
import ExecutionEnvironment from 'fbjs/lib/ExecutionEnvironment';
import fdpHelper from 'utils/FDPHelper';
import { Timeout } from 'actions/ApiCaller/Timeout';
import metricsTracker from 'utils/MetricsTracker';
import NewApiCaller from './ApiCaller/ApiCaller';
const MAX_CONSECUTIVE_ACKS = 3;
// extract necessary fetchOptions from config
// https://fetch.spec.whatwg.org/#requestinit
const extracts = [
    'method',
    'headers',
    'body',
    'referrer',
    'mode',
    'credentials',
    'cache',
    'redirect',
    'integrity',
    'window'
];
export const stringifyError = (error, config) => {
    let errMsg = `Unknown error in API call | ${config.pathname}`;
    // prettier-ignore
    if (error.status) { //tslint:disable-line:prefer-conditional-expression
        // it is a network error
        errMsg = `Network error | ${error.statusText} | status: ${error.status} | path: ${config.pathname}`;
    }
    else {
        // it is a js error
        errMsg = `JS error | ${error.message} | path: ${config.pathname}`;
    }
    return errMsg;
};
export const JSON_PARSING_ERROR = 'JSON_PARSING_ERROR';
export const DEFAULT_SENTRY_OPTIONS = {
    tags: { errorOrigin: 'apiCaller' }
};
export const sendToSentry = (error, config) => {
    if (ExecutionEnvironment.canUseDOM && config.sendToSentry !== false) {
        const isSetup = get(window, 'Raven.isSetup', null);
        const errMsg = stringifyError(error, config);
        let options = Object.assign({}, DEFAULT_SENTRY_OPTIONS, { extra: { config, error, errMsg } });
        if (typeof isSetup === 'function' && window.Raven.isSetup()) {
            // The "fingerprint" option allows grouping of the issues based on
            // that criteria. This helps in NOT listing the json parse failures
            // in the mainline sentry listing
            options =
                error.statuscode === JSON_PARSING_ERROR
                    ? Object.assign({}, options, { fingerprint: [JSON_PARSING_ERROR] })
                    : options;
            window.Raven.captureException(error, options);
        }
    }
};
const resolveWithJsonAndStatusCode = (response, requestUrl) => {
    const statuscode = response.status || 'UNKNOWN_STATUS_CODE';
    const xReqId = response.headers.get('X-Request-ID');
    const xParentReqId = response.headers.get('X-Parent-Request-ID');
    return response.json().then(resp => {
        const extraData = xReqId || xParentReqId ? { statuscode, xReqId, xParentReqId } : { statuscode };
        const respwithsc = Object.assign({}, resp, extraData);
        return Promise.resolve(respwithsc);
    }, (err) => {
        // This happens when JSON.parse step had failed due to empty body
        // or other reasons. The `err` object here mostly is a Error object
        // in these cases.
        //
        // TODO: May be map this to sentry so that we can inform upstream
        // services? If so, we might have to add other contextual objects like
        // error, request and response metadata etc.
        const respwithsc = Object.assign({}, err, { statuscode: 'JSON_PARSING_ERROR', requestUrl });
        return Promise.reject(respwithsc);
    });
};
const rejectWithJsonAndStatusCode = (response, requestUrl) => {
    // Reuse the resolve function above, and just force-reject from the success
    // callback, which is same as repeating the entire function again, with
    // only a change inside the `json()` callback
    return resolveWithJsonAndStatusCode(response, requestUrl).then(response => Promise.reject(response));
};
const formatUrl = (config) => {
    let queryString = '';
    if (config.query) {
        queryString = config.useQueryEncoding
            ? objectToQueryParams(config.query)
            : objectToQueryParams(config.query, x => x.toString());
    }
    const { protocol, port, hostname, pathname } = config;
    const portComponent = port ? `:${port}` : '';
    return `${protocol}://${hostname}${portComponent}${pathname}${queryString}`;
};
export const UNKNOWN_ACTION = 'unknown';
class ApiCaller {
    constructor() {
        this.xhr = (options, token) => {
            const xhr = new XMLHttpRequest();
            xhr.open(options.method || 'GET', options.url, true);
            xhr.withCredentials = true;
            if (options.headers) {
                Object.getOwnPropertyNames(options.headers).forEach(key => {
                    xhr.setRequestHeader(key, options.headers[key]);
                });
            }
            if (typeof options.onProgress === 'function') {
                xhr.upload.addEventListener('progress', (e) => {
                    options.onProgress(Math.ceil((e.loaded * 100) / e.total));
                });
            }
            return new Promise((resolve, reject) => {
                xhr.addEventListener('readystatechange', () => {
                    if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
                        let response = JSON.parse(xhr.response);
                        resolve(response);
                    }
                    else if (xhr.readyState === 4 && xhr.status >= 300) {
                        reject(xhr);
                    }
                });
                xhr.addEventListener('error', () => reject(xhr));
                if (token) {
                    token.cancel = function () {
                        xhr.abort();
                        reject(xhr);
                    };
                }
                xhr.send(options.data);
            });
        };
        if (__SERVER__) {
            const http = require('http'); // tslint:disable-line: no-require-imports
            this.agent = new http.Agent({
                keepAlive: true,
                maxSockets: 24
            });
        }
        this.responseAcks = 0;
    }
    toString() {
        return 'ApiCaller';
    }
    get(config) {
        config.method = 'GET';
        return this._request(config, null);
    }
    post(config, _reqBody) {
        config.method = 'POST';
        let reqBody;
        // prettier-ignore
        if (typeof _reqBody === 'string' || config.multipart) { // tslint:disable-line:prefer-conditional-expression
            reqBody = _reqBody;
        }
        else {
            reqBody = JSON.stringify(_reqBody);
        }
        return this._request(config, reqBody);
    }
    put(config, _reqBody) {
        config.method = 'PUT';
        const reqBody = typeof _reqBody === 'string' ? _reqBody : JSON.stringify(_reqBody);
        return this._request(config, reqBody);
    }
    delete(config) {
        config.method = 'DELETE';
        return this._request(config, null);
    }
    _request(config, reqBody) {
        const acceptJson = typeof config.acceptJson !== 'undefined' ? config.acceptJson : true;
        const acceptErrors = typeof config.acceptErrors !== 'undefined' ? config.acceptErrors : false;
        let fetchOptions = pick(config, extracts);
        fetchOptions.agent = this.agent;
        const metricsTag = config.action ? `action.${config.action}` : UNKNOWN_ACTION;
        if (reqBody) {
            fetchOptions.body = reqBody;
        }
        metricsTracker.startTimer(metricsTag);
        const requestUrl = formatUrl(config);
        const actualPromise = fetch(requestUrl, fetchOptions)
            .then((response) => {
            metricsTracker.endTimer(metricsTag);
            if (response.ok) {
                // send the header for tracking
                this._sendAckInTracker(response.headers, fetchOptions.headers);
                // Either parse the JSON, or pass the raw response down to
                // the next caller. If user wants the parsed JSON, also
                // add-in the statuscode in that response.
                return acceptJson ? resolveWithJsonAndStatusCode(response, requestUrl) : Promise.resolve(response);
            }
            if (acceptErrors && acceptJson) {
                // Since users want the parsed and resolved JSON, let's add
                // statuscode to that object and reject that object
                return rejectWithJsonAndStatusCode(response, requestUrl);
            }
            // Passing this raw response object to the reject chain
            return Promise.reject(response);
        })
            .then((response) => Promise.resolve(response), (error) => {
            sendToSentry(error, config);
            if (!acceptErrors) {
                throw stringifyError(error, config);
            }
            else {
                return Promise.reject(error);
            }
        });
        const timeout = new Timeout(config);
        return Promise.race([actualPromise, timeout.start]).then(x => {
            timeout.clear();
            return Promise.resolve(x);
        }, y => {
            timeout.clear();
            return Promise.reject(y);
        });
    }
    // this is a response acknowledgement tracking
    // if the value of ack-response header is true we send request-id to fdp track event which will be batched and sent later
    _sendAckInTracker(responseHeaders, requestHeaders) {
        const isAckResponse = responseHeaders.get('X-ACK-RESPONSE');
        const requestId = responseHeaders.get('X-Request-ID');
        // we do not want to send any more events in case there is a cycle of response and response acks
        if (this.responseAcks > MAX_CONSECUTIVE_ACKS) {
            return;
        }
        if (isAckResponse && requestId) {
            fdpHelper.setHeaders(requestHeaders);
            fdpHelper.trackEvent({
                rid: requestId,
                en: 'RA'
            });
            this.responseAcks++;
            metricsTracker.setCount('action.FDP_RA');
        }
        else {
            this.responseAcks = 0;
        }
    }
}
export function getBlobUrl(blobLink, options) {
    return fetch(blobLink, options)
        .then(response => {
        return response.blob();
    })
        .then(blob => {
        return URL.createObjectURL(blob);
    });
}
export const OldApiCaller = new ApiCaller();
export default NewApiCaller;
